import React, { useEffect } from 'react'
import { Alert, Button, Flex, Form, Input, InputNumber, Select, Radio, Switch } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import Access from '../../../components/access/Access'
import { AssignmentPermissions } from '../../../../../constants/assignment-permissions'
import Text from 'antd/es/typography/Text'
import MarkdownRenderer from '../../../../../components/common/markdown-renderer'
import { flatten } from '../../../../../utils/array.helper'

export const TextSubmissionTypes = {
    QUIZ: 1,
    DOCUMENT: 2,
}

const QuestionForm = ({
    onSubmit,
    initialValues,
    error,
    loading,
    earlierQuestions,
    formResetTrigger,
    questions,
    form: externalForm,
    submitButtonText = 'Submit',
}) => {
    const [form] = Form.useForm(externalForm)

    const dependsOnOptions =
        earlierQuestions?.map(question => ({
            label: question.name || question.content,
            value: question.id,
        })) || []

    const typeOptions = [
        { label: 'Quiz', value: TextSubmissionTypes.QUIZ },
        { label: 'Document', value: TextSubmissionTypes.DOCUMENT },
    ]

    useEffect(() => {
        form.resetFields()
    }, [form, formResetTrigger])

    const isValueUnique = (value, field, currentQuestionId) => {
        if (!value || !questions) return true
        return !flatten(questions, { excludeParents: true }).some(
            question => question[field]?.toLowerCase() === value.toLowerCase() && question.id !== currentQuestionId,
        )
    }

    const validateQuestionField = (field, value, currentQuestionId) => {
        if (!isValueUnique(value, field, currentQuestionId))
            return Promise.reject(`Question ${field} must be unique among all questions`)
        return Promise.resolve()
    }

    const isParentQuestion = initialValues?.children
    const isChildQuestion = initialValues?.parentId

    const handleSubmit = async values => {
        const result = await onSubmit(values)

        // If submission was successful, reset touched state but keep values
        if (result !== false) form.setFields(Object.keys(form.getFieldsValue()).map(name => ({ name, touched: false })))

        return result
    }

    return (
        <Form
            form={form}
            name='question-form'
            autoComplete='off'
            onFinish={handleSubmit}
            requiredMark={false}
            layout='vertical'
        >
            <Form.Item
                name='name'
                label='Name'
                initialValue={initialValues?.name}
                rules={[{ validator: (rule, value) => validateQuestionField('name', value, initialValues?.id) }]}
            >
                <Access
                    permissions={[AssignmentPermissions.CAN_EDIT]}
                    fallback={<Text>{initialValues?.name || 'No name provided'}</Text>}
                >
                    <Input placeholder='Enter question name (otherwise the content will be used as a name)' />
                </Access>
            </Form.Item>
            {!isParentQuestion && (
                <Form.Item
                    name='content'
                    label='Content'
                    rules={[
                        {
                            required: true,
                            message: 'Please, enter content',
                        },
                        { validator: (rule, value) => validateQuestionField('content', value, initialValues?.id) },
                    ]}
                    initialValue={initialValues?.content}
                >
                    <Access
                        permissions={[AssignmentPermissions.CAN_EDIT]}
                        fallback={<Text>{initialValues?.content || 'No content provided'}</Text>}
                    >
                        <TextArea autoSize placeholder='Enter content' />
                    </Access>
                </Form.Item>
            )}
            {!isParentQuestion && (
                <Form.Item
                    name='gradingReferenceContent'
                    label='Grading reference content'
                    initialValue={initialValues?.gradingReferenceContent}
                    tooltip='Markdown is supported: # Heading, **Bold text**, - List item, 1. Numbered item'
                >
                    <Access
                        permissions={[AssignmentPermissions.CAN_EDIT]}
                        fallback={
                            <MarkdownRenderer
                                content={
                                    initialValues?.gradingReferenceContent || 'No grading reference content provided'
                                }
                            />
                        }
                    >
                        <TextArea autoSize placeholder='Enter grading reference content' />
                    </Access>
                </Form.Item>
            )}
            <Flex justify='space-between'>
                <Form.Item name='type' label='Type' initialValue={initialValues?.type || TextSubmissionTypes.QUIZ}>
                    <Access
                        permissions={[AssignmentPermissions.CAN_EDIT]}
                        fallback={<Text>{initialValues?.type === TextSubmissionTypes.QUIZ ? 'Quiz' : 'Document'}</Text>}
                    >
                        <Radio.Group options={typeOptions} optionType='button' />
                    </Access>
                </Form.Item>
                {!isChildQuestion && (
                    <Form.Item
                        name='wordsLimit'
                        label='Word limit'
                        rules={[{ type: 'number', min: 0 }]}
                        initialValue={initialValues?.wordsLimit}
                    >
                        <Access
                            permissions={[AssignmentPermissions.CAN_EDIT]}
                            fallback={<Text>{initialValues?.wordsLimit || 'No word limit set'}</Text>}
                        >
                            <InputNumber addonAfter='words' style={{ width: 144 }} step={10} />
                        </Access>
                    </Form.Item>
                )}
                {!isParentQuestion && (
                    <Form.Item
                        name='weight'
                        label='Weight'
                        rules={[{ type: 'number', min: 0 }]}
                        initialValue={initialValues?.weight}
                    >
                        <Access
                            permissions={[AssignmentPermissions.CAN_EDIT]}
                            fallback={<Text>{initialValues?.weight || 'No weight set'}</Text>}
                        >
                            <InputNumber style={{ width: 144 }} />
                        </Access>
                    </Form.Item>
                )}
                {!isChildQuestion && (
                    <Form.Item name='isGlobal' label='Global' initialValue={initialValues?.isGlobal}>
                        <Access
                            permissions={[AssignmentPermissions.CAN_EDIT]}
                            fallback={<Text>{initialValues?.isGlobal ? 'Yes' : 'No'}</Text>}
                        >
                            <Switch />
                        </Access>
                    </Form.Item>
                )}
            </Flex>

            {Boolean(dependsOnOptions.length) && !isParentQuestion && (
                <Form.Item
                    name='dependsOnQuestionIds'
                    label='Depends on'
                    initialValue={initialValues?.dependsOnQuestionIds}
                >
                    <Access
                        permissions={[AssignmentPermissions.CAN_EDIT]}
                        fallback={
                            <Text>
                                {initialValues?.dependsOnQuestionIds?.length
                                    ? dependsOnOptions
                                          .filter(option => initialValues.dependsOnQuestionIds.includes(option.value))
                                          .map(option => option.label)
                                          .join(', ')
                                    : 'This question does not depend on any other questions'}
                            </Text>
                        }
                    >
                        <Select
                            mode='multiple'
                            allowClear
                            style={{ width: '100%' }}
                            placeholder='Select the earlier questions this question depends on if any'
                            options={dependsOnOptions}
                            maxCount={1}
                            showSearch
                            optionFilterProp='label'
                            popupMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentNode}
                        />
                    </Access>
                </Form.Item>
            )}
            {error && <Alert message={error} type='error' showIcon closable />}
            <Form.Item>
                <Access permissions={[AssignmentPermissions.CAN_EDIT]}>
                    <Button type='primary' htmlType='submit' block loading={loading}>
                        {submitButtonText || 'Save'}
                    </Button>
                </Access>
            </Form.Item>
        </Form>
    )
}

export default QuestionForm
